import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid2, Typography } from "@mui/material";
import ChartDrillDownModalV2 from "../../BmsFaults/Analytics/ChartDrillDown/ChartDrillDownModalV2";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import DigitalTwinTableSearch from "../../../Components/DigitalTwinTableSearch";
import { getSmartAlertsList, smartAlertsListAction } from "../../../Api/SmartAlertsAnalyticsApi";
import { genericSearchSuggestions } from "../../../Api/DigitalTwinListApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { deviceSearchFilter } from "./drillDownFilters";
import {
  SMART_ALERTS_META_INFO_FIELDS,
  deriveMetaInfoFields,
  pickMetaInfoValues,
} from "../../../Helper/Analytics/tableMetaInfo";

const RESOLUTION_COLUMN_KEYS = ["autoResolvedAlertCount", "manualResolvedAlertCount"];

const COUNT_KEY_TO_DATAINDEX = {
  "autoResolvedAlertCount":   "AutoResolvedCount",
  "manualResolvedAlertCount": "ManualResolvedCount",
};

const filterByResolutionType = (columnsConfig, columnOrdering, seriesName) => {
  const keep = seriesName === "Auto Resolved" ? "autoResolvedAlertCount" : "manualResolvedAlertCount";
  const filteredConfig = {};
  for (const key in columnsConfig) {
    if (RESOLUTION_COLUMN_KEYS.includes(key) && key !== keep) continue;
    filteredConfig[key] = columnsConfig[key];
  }
  const filteredOrder = (columnOrdering || []).filter(
    (key) => !RESOLUTION_COLUMN_KEYS.includes(key) || key === keep
  );
  return { columnsConfig: filteredConfig, columnOrdering: filteredOrder };
};

const AutoResolvedDrillDownModal = ({ open, onClose, selectedDataPoint, chartTitle, startDate, endDate, globalFilters = {} }) => {
  const pagesContent = usePagesContent();
  const summaryMeta  = pagesContent?.metaInfo?.table?.autoResolvedSummaryTable;
  const vehiclesMeta = pagesContent?.metaInfo?.table?.autoResolvedVehiclesTable;
  const seriesName   = selectedDataPoint?.seriesName || "";

  const isAuto            = seriesName !== "Manually Resolved";
  const resolutionApiValue = isAuto ? "AutoResolved" : "ManualResolved";
  const countColumnKey    = isAuto ? "autoResolvedAlertCount" : "manualResolvedAlertCount";

  const [level2SearchTerm, setLevel2SearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });

  useEffect(() => {
    if (!open) setLevel2SearchTerm({ selectedDevices: [], selectedSearchColumn: null });
  }, [open]);

  const handleLevel2Search = useCallback((selectedDevices, selectedSearchColumn) => {
    setLevel2SearchTerm({ selectedDevices, selectedSearchColumn });
  }, []);

  const searchSuggestionsHandler = useCallback(
    (typedValue, _columnName, limit) => genericSearchSuggestions(typedValue, "licensePlateNo", limit),
    []
  );

  const level1TableConfigs = summaryMeta ? (() => {
    const { columnsConfig, columnOrdering } = filterByResolutionType(
      summaryMeta.columnsConfig, summaryMeta.columnOrdering, seriesName
    );
    return {
      tableColumnsConfig: columnsConfig,
      tableColumnOrder:   columnOrdering,
      title: (dp) => `${dp?.seriesName || "Resolved"} Alerts`,
      emptyMessage: "No alerts found",
    };
  })() : null;

  // What the vehicles table asks the API for, and reads back, follows the columns
  // it is configured to show. Memoised on the config so the callbacks below keep a
  // stable identity and do not refetch on every render.
  const level2MetaInfoFields = useMemo(
    () => deriveMetaInfoFields(vehiclesMeta?.columnsConfig, SMART_ALERTS_META_INFO_FIELDS),
    [vehiclesMeta]
  );

  const level2TableConfigs = vehiclesMeta ? (() => {
    const { columnsConfig, columnOrdering } = filterByResolutionType(
      vehiclesMeta.columnsConfig, vehiclesMeta.columnOrdering, seriesName
    );
    return {
      tableColumnsConfig: columnsConfig,
      tableColumnOrder:   columnOrdering,
      title: "Vehicles Affected",
      emptyMessage: "No vehicles found",
    };
  })() : null;

  const getLevel1TableData = useCallback(async (_dp, pageNumber, sortBy) => {
    if (!startDate || !endDate) return { data: [], total: 0 };

    const result = await getSmartAlertsList(globalFilters, {
      startDate,
      endDate,
      select:    ["ResolvedAlertCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration"],
      groupBy:   ["alertDescription"],
      skipPagination: false,
      pageNum:        pageNumber || 1,
      pageCount:      10,
      ...(sortBy?.length ? { sortBy } : {}),
      filters: [{ name: "ResolutionType", values: [resolutionApiValue] }],
    });

    if (result?.responseStatus?.code !== CODE.SUCCESS) return { data: [], total: 0 };

    const rows = (result.response?.data || []).map((row) => {
      const metrics = row.metricsArray || [];
      return {
        id:                    row.xLabel,
        xLabel:                row.xLabel,
        alertName:             row.xLabel,
        [COUNT_KEY_TO_DATAINDEX[countColumnKey] || countColumnKey]: metrics.find((m) => m.type === "ResolvedAlertCount")?.value    ?? 0,
        avgResolutionDuration: metrics.find((m) => m.type === "avgResolutionDuration")?.value ?? 0,
        minResolutionDuration: metrics.find((m) => m.type === "minResolutionDuration")?.value ?? 0,
        maxResolutionDuration: metrics.find((m) => m.type === "maxResolutionDuration")?.value ?? 0,
      };
    });

    return { data: rows, total: result.response?.total ?? rows.length };
  }, [globalFilters, startDate, endDate, resolutionApiValue, countColumnKey]);

  const getLevel2TableData = useCallback(async (level1Record, pageNumber, sortBy) => {
    if (!startDate || !endDate) return { data: [], total: 0 };

    const { selectedDevices } = level2SearchTerm;
    const filters = [
      { name: "ResolutionType", values: [resolutionApiValue] },
      ...(level1Record?.alertName ? [{ name: "alertDescription", values: [level1Record.alertName] }] : []),
      ...deviceSearchFilter(selectedDevices),
    ];

    const result = await getSmartAlertsList(globalFilters, {
      startDate,
      endDate,
      select:    ["ResolvedAlertCount"],
      groupBy:   ["batteryID"],
      metaInfo:  level2MetaInfoFields,
      skipPagination: false,
      pageNum:        pageNumber || 1,
      pageCount:      10,
      ...(sortBy?.length ? { sortBy } : {}),
      filters,
    });

    if (result?.responseStatus?.code !== CODE.SUCCESS) return { data: [], total: 0 };

    const rows = (result.response?.data || []).map((row) => {
      const metrics = row.metricsArray || [];
      const count   = metrics.find((m) => m.type === "ResolvedAlertCount")?.value ?? 0;
      return {
        id:                row.xLabel,
        ...pickMetaInfoValues(row, level2MetaInfoFields),
        [COUNT_KEY_TO_DATAINDEX[countColumnKey] || countColumnKey]: count,
      };
    });

    return { data: rows, total: result.response?.total ?? rows.length };
  }, [globalFilters, startDate, endDate, resolutionApiValue, countColumnKey, level2SearchTerm, level2MetaInfoFields]);

  const buildCsvColumns = (tableColumns) =>
    tableColumns
      .filter((col) => col.dataIndex !== "actions")
      .map((col) => ({
        dataIndex:   col.dataIndex === "alertName" ? "xLabel" : col.dataIndex,
        headerLabel: col.headerLabel || col.title,
      }));

  const applyLevel1TableAction = useCallback(async (rowSelectionKeys, actionType, selectAll, params) => {
    const { columns: tCols, emailTo, emailCc, subject, body } = params;
    const csvColumns = buildCsvColumns(tCols);
    const result = await smartAlertsListAction(globalFilters, {
      actionType:  actionType === "download" ? "download-csv" : "mail",
      startDate,
      endDate,
      select:      ["ResolvedAlertCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration"],
      groupBy:     ["alertDescription"],
      filters:     [{ name: "ResolutionType", values: [resolutionApiValue] }],
      selectAll:   selectAll || rowSelectionKeys.length === 0,
      resourceIDs: selectAll ? [] : rowSelectionKeys,
      fileName:    "resolution_summary",
      columns:     csvColumns,
      columnOrder: csvColumns.map((c) => c.headerLabel),
      ...(actionType === "mail" ? { emailTo, emailCc, subject, body } : {}),
    });
    if (actionType === "download") return result?.response?.data || [];
  }, [globalFilters, startDate, endDate, resolutionApiValue]);

  const applyLevel2TableAction = useCallback(async (rowSelectionKeys, actionType, selectAll, params, level1Record) => {
    const { columns: tCols, emailTo, emailCc, subject, body } = params;
    const csvColumns = buildCsvColumns(tCols);
    const { selectedDevices } = level2SearchTerm;
    const filters = [
      { name: "ResolutionType", values: [resolutionApiValue] },
      ...(level1Record?.alertName ? [{ name: "alertDescription", values: [level1Record.alertName] }] : []),
      ...deviceSearchFilter(selectedDevices),
    ];
    const result = await smartAlertsListAction(globalFilters, {
      actionType:  actionType === "download" ? "download-csv" : "mail",
      startDate,
      endDate,
      select:      ["ResolvedAlertCount"],
      groupBy:     ["batteryID"],
      metaInfo:    level2MetaInfoFields,
      filters,
      selectAll:   selectAll || rowSelectionKeys.length === 0,
      resourceIDs: selectAll ? [] : rowSelectionKeys,
      fileName:    "resolution_vehicles",
      columns:     csvColumns,
      columnOrder: csvColumns.map((c) => c.headerLabel),
      ...(actionType === "mail" ? { emailTo, emailCc, subject, body } : {}),
    });
    if (actionType === "download") return result?.response?.data || [];
  }, [globalFilters, startDate, endDate, resolutionApiValue, level2SearchTerm, level2MetaInfoFields]);

  const vehiclesSearchBar = vehiclesMeta?.searchBar;
  const level2SearchComponent = vehiclesSearchBar?.isVisible ? (
    <DigitalTwinTableSearch
      onSearch={handleLevel2Search}
      containerWidth="80%"
      searchableFields={(vehiclesSearchBar.searchableFields || []).map((f) => ({
        key: f.key,
        title: f.label,
      }))}
      searchSuggestionsHandler={searchSuggestionsHandler}
    />
  ) : null;

  if (!level1TableConfigs) return null;

  return (
    <ChartDrillDownModalV2
      open={open}
      onClose={onClose}
      disableColumnSettings={false}
      alertsSubTab="platform-alerts"
      title={(dp) => {
        const base   = chartTitle || "Alert Resolution Trend (Auto vs Manual)";
        const series = dp?.seriesName || "";
        const date   = dp?.label || "";
        return (
          <Grid2 container gap={1} alignItems="center">
            <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
              {base}
              {series && (
                <Typography component="span" variant="fw500s16c000000" sx={{ lineHeight: "28px" }}>
                  {" / "}{series}
                </Typography>
              )}
            </Typography>
            {date && (
              <>
                <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{"•"}</Typography>
                <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{date}</Typography>
              </>
            )}
          </Grid2>
        );
      }}
      selectedDataPoint={selectedDataPoint}
      tableConfigs={level1TableConfigs}
      getTableData={getLevel1TableData}
      applyTableAction={applyLevel1TableAction}
      getCSVFileName={() => "resolution_summary"}
      level2TableConfigs={level2TableConfigs}
      level2GetTableData={getLevel2TableData}
      level2ApplyTableAction={applyLevel2TableAction}
      level2GetCSVFileName={() => "resolution_vehicles"}
      level2SearchComponent={level2SearchComponent}
      level2Title={(record) => (
        <Grid2 container gap={1} alignItems="center">
          <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: "28px" }}>
            {record?.alertName || "Alert"}
            <Typography component="span" variant="fw400s14c000000" sx={{ lineHeight: "28px" }}>
              {" / Details"}
            </Typography>
          </Typography>
        </Grid2>
      )}
    />
  );
};

export default AutoResolvedDrillDownModal;
